import React, { useState, useEffect } from "react"
import { IoIosArrowRoundBack } from "react-icons/io"
import { useHistory } from "react-router-dom"
import { ErrorNotification, SuccessNotification } from "../../utils"
import api, { BASE_URL } from "../../utils/api"
import Spinner from "../../components/Spinner"
import { RxPencil1 } from "react-icons/rx"
import { FiPlusCircle } from "react-icons/fi"
import { Tooltip } from "../../layout/sidebar"
import DeleteGroupModal from "../../components/modals/DeleteGroupModal"
import Pagination from "../../components/Pagination"
import AddGroupMembersModal from "../../components/groups/AddGroupMembersModal"

export default function GroupDetail(props) {
  const history = useHistory()
  const request = props?.location?.state
  const [show, setShow] = React.useState(false)
  const [group, setGroup] = useState()
  const [groupMembers, setGroupMembers] = useState()
  const [loading, setLoading] = useState(true)
  const [membersLoading, setMembersLoading] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selected, setSelected] = useState()
  const [deleting, setDeleting] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [pageSize] = useState(10)


  const getGroupDetail = async () => {
    const groupId = request?.state?.id || group?.id
    if (!groupId) return

    setLoading(true)
    try {
      const response = await api.get(`groups/${groupId}/`)
      setGroup(response.data)
      setLoading(false)
    } catch (error) {
      console.error('Error fetching group detail:', error)
      setLoading(false)
    }
  }

  const getGroupMembers = async () => {
    const groupId = request?.state?.id || group?.id
    if (!groupId) return

    setMembersLoading(true)
    try {
      const response = await api.get(`groups/${groupId}/members`, {
        params: {
          page: currentPage,
          page_size: pageSize
        }
      })
      
      // Standard DRF pagination
      if (response?.data?.results) {
        setGroupMembers(response.data.results)
        setTotalCount(response.data.count || 0)
      } 
      // Fallback for non-standard paginated response or specific members key
      else if (response?.data?.members) {
        setGroupMembers(response.data.members)
        setTotalCount(response.data.count || response.data.members.length || 0)
      }
      // Fallback for direct array response
      else {
        setGroupMembers(Array.isArray(response.data) ? response.data : [])
        setTotalCount(Array.isArray(response.data) ? response.data.length : 0)
      }
      setMembersLoading(false)
    } catch (error) {
      console.error('Error fetching group members:', error)
      setMembersLoading(false)
    }
  }

  const removeMember = (member) => {
    const token = localStorage.getItem("token")
    setDeleting(true)
    fetch(`${BASE_URL}groups/${group.id}/remove_member/`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        member_type: member.member_type === "farmers" ? "farmer" : "user",
        member_id: member?.member_details?.id,
      }),
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to remove member')
        return res.json()
      })
      .then(() => {
        SuccessNotification("You have successfully removed a member")
        getGroupMembers()
        setDeleting(false)
        setShowConfirmModal(false)
      })
      .catch(() => {
        ErrorNotification("An error occurred while removing member")
        setDeleting(false)
      })
  }

  useEffect(() => {
    getGroupDetail()
  }, [])

  useEffect(() => {
    getGroupMembers()
  }, [currentPage])

  return (
    <>
      {showConfirmModal && (
        <DeleteGroupModal
          setShow={setShowConfirmModal}
          show={showConfirmModal}
          handleDelete={() => removeMember(selected)}
          title="Delete Group Member?"
          description="Are you sure you want to delete this member (Participant) from the group?"
          loading={deleting}
          label="Member"
        />
      )}

      {show && (
        <AddGroupMembersModal
          groupId={request?.state?.id || group?.id}
          groupType={group?.group_type}
          existingMembers={groupMembers}
          onClose={() => setShow(false)}
          onSuccess={() => {
            getGroupMembers()
            getGroupDetail()
          }}
        />
      )}

      {loading && (
        <div className="w-full h-96 flex items-center justify-center">
          <Spinner />
        </div>
      )}
      {!loading && (
        <div className="w-full lg:px-10 px-2 h-full py-6 lg:py-10 space-y-6">
          <div className="w-full flex flex-col space-y-2 md:flex-row md:items-center md:justify-between">
            <div className="w-full">
              <button onClick={() => history.goBack()} className="flex flex-row items-center">
                <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
                <p className="text-sm font-light capitalize dark:text-white">
                  Back
                </p>
              </button>

              <h2 className="text-2xl font-bold dark:text-white">
                Group Details
              </h2>
            </div>

            <div className=" flex flex-col md:flex-row gap-2 w-full md:justify-end">
              <button
                onClick={() => history.push("/admin/edit-group", {
                  state: {
                    id: group?.id,
                    ...group
                  }
                })}
                className="border bg-white border-yellowcolor hover:border-yellowcolor/80 text-yellowcolor px-6 flex flex-row items-center capitalize gap-2 py-2 rounded-md dark:bg-dark"
              >
                <RxPencil1 size={20} />
                Update Group
              </button>

              <button
                onClick={() => setShow(true)}
                className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 capitalize rounded-md"
              >
                <FiPlusCircle size={20} />
                Add Group Member
              </button>
            </div>
          </div>


          <div className="bg-white px-4 py-2 mt-2 rounded-lg space-y-6 dark:bg-dark">
            <div className="flex flex-col md:flex-row md:items-center justify-between mt-5">
              <p className="text-lg text-yellowcolor capitalize">
                Group Information
              </p>

              <p className="text-base text-yellowcolor capitalize">
                Last updated: {group?.updated_at ? new Date(group.updated_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : group?.created_at ? new Date(group.created_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : "N/A"}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="w-full flex flex-row items-center justify-between bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-lg p-4 space-y-1">
                <div className="flex-col space-y-1">
                  <p className="text-sm text-black dark:text-white capitalize">
                    Group Name
                  </p>
                  <p className="text-sm text-gray-500 capitalize">{group?.group_name}</p>
                </div>
                <div className="flex-col space-y-1">
                  <p className="text-sm text-black dark:text-white capitalize">
                    Created on
                  </p>
                  <p className="text-sm text-gray-500 capitalize">
                    {group?.created_at ? new Date(group.created_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : "-"}
                  </p>
                </div>
              </div>
              <div className="w-full flex flex-col bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-lg p-4 space-y-1">
                <p className="text-sm text-black dark:text-white capitalize">
                  Description
                </p>
                <p className="text-sm text-gray-500 capitalize">
                  {group?.description || "N/A"}
                </p>
              </div>
              <div className="w-full flex flex-col bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-lg p-4 space-y-1">
                <p className="text-sm text-black dark:text-white capitalize">
                  Number Of Members (Participants)
                </p>
                <p className="text-sm text-gray-500 capitalize">
                  {group?.total_members || 0}
                </p>
              </div>
            </div>

            <p className="text-lg text-yellowcolor capitalize">
              Group Members
            </p>


            <div className="my-5">
              <div className="flex flex-col">
                <div className="-m-1.5 overflow-x-auto">
                  <div className="p-1.5 min-w-full inline-block align-middle">
                    <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-md overflow-hidden ">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                        <thead className="bg-transparent dark:bg-secondaryDark">
                          <tr>
                          
                            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500  capitalize hover:text-border-dark dark:hover:text-border-dark  ">Name</th>
                            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Region</th>
                            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">District</th>
                            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Community</th>
                            <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize hover:text-black dark:hover:text-border-dark ">Phone Number</th>
                            <th scope="col" className="px-6 py-3 text-end text-sm font-light text-gray-500  capitalize hover:text-black ">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                          {membersLoading ? (
                            <tr>
                              <td colSpan={6} className="px-6 py-10 text-center">
                                <div className="flex flex-col items-center gap-2">
                                  <Spinner size="sm" />
                                  <span className="text-gray-500 text-sm">Loading members...</span>
                                </div>
                              </td>
                            </tr>
                          ) : groupMembers?.length > 0 ? (
                            groupMembers.map((data, index) => (
                              <tr key={index} className="text-[13px]">
                                <td className="px-6 hover:bg-gray-50 dark:hover:bg-secondaryDark py-4 whitespace-nowrap text-sm  font-medium text-gray-800 dark:text-neutral-200 flex flex-row items-center gap-2 capitalize">
                                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200">
                                    <p className="text-sm uppercase font-bold text-black">
                                      {data?.member_name?.split(' ').map(n => n[0]).join('')}
                                    </p>
                                  </div>
                                  {data?.member_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap font-light">
                                  {data?.member_details?.location?.split(',')[2] || "-"}
                                </td>
                                <td className="px-6 py-4 capitalize whitespace-nowrap font-light">
                                  {data?.member_details?.location?.split(',')[1] || "-"}
                                </td>
                                <td className="px-6 py-4 capitalize whitespace-nowrap text-end font-light">
                                  {data?.member_details?.location?.split(',')[0] || "-"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                                  {data?.member_details?.phone || "-"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                                  <div className="flex flex-row items-center gap-4">
                                    <Tooltip text="Delete" position="top">
                                      <button
                                        onClick={() => {
                                          setSelected(data)
                                          setShowConfirmModal(true)
                                        }}
                                        className="">
                                        <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                          <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                          <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                          <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                          <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                      </button>
                                    </Tooltip>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                                {membersLoading ? "" : "No members found in this group."}
                              </td>
                            </tr>
                          )}
                        </tbody>

                      </table>
                      {totalCount > pageSize && (
                        <Pagination
                          currentPage={currentPage}
                          totalPages={Math.ceil(totalCount / pageSize)}
                          onPageChange={setCurrentPage}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="h-10" ></div>


          </div>


        </div>
      )}
    </>
  )
}
